import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styles from './ConfirmModal.module.css';
import { X } from 'lucide-react';
import { tr } from '@/i18n';

interface ConfirmModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (inputValue?: string) => void;
    title: string;
    description: string;
    confirmText: string;
    cancelText?: string;
    hasInput?: boolean;
    /** When false, the input may be left empty (optional comment). Defaults to true. */
    inputRequired?: boolean;
    inputLabel?: string;
    inputPlaceholder?: string;
    /** Minimum trimmed length of a required input (e.g. a rejection reason). */
    inputMinLength?: number;
    isDanger?: boolean;
    /** Extra content under the description (e.g. a warning list). */
    children?: React.ReactNode;
}

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    description,
    confirmText,
    cancelText = tr("common.cancel", "Cancel"),
    hasInput = false,
    inputRequired = true,
    inputLabel = tr("ui.comment", "Comment"),
    inputPlaceholder = tr("ui.enter_text", "Enter text..."),
    inputMinLength = 1,
    isDanger = false,
    children,
}) => {
    const [inputValue, setInputValue] = useState('');

    useEffect(() => {
        if (isOpen) {
            setInputValue('');
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const minLength = Math.max(1, inputMinLength);
    const typed = inputValue.trim().length;
    const inputTooShort = hasInput && inputRequired && typed < minLength;
    // Say why the confirm button is off instead of leaving a silent disabled button.
    const showCounter = hasInput && inputRequired && minLength > 1;

    const handleConfirmClick = () => {
        if (inputTooShort) {
            return;
        }
        onConfirm(hasInput ? inputValue : undefined);
    };

    return createPortal(
        <div className={styles.modalOverlay} onClick={onClose}>
            <div
                className={styles.modalWrapper}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label={typeof title === 'string' ? title : undefined}
            >
                <div className={styles.modalHead}>
                    <h3 className={styles.modalTitle}>{title}</h3>
                    <button type="button" className={styles.modalCloseBtn} onClick={onClose} aria-label={tr("common.close", "Close")}>
                        <X size={18} aria-hidden="true" />
                    </button>
                </div>

                <div className={styles.modalBody}>
                    <p className={styles.modalText}>{description}</p>
                    {children}

                    {hasInput && (
                        <div className={styles.modalInputWrapper}>
                            <label className={styles.modalInputLabel} htmlFor="confirm-modal-input">
                                {inputLabel}{inputRequired && <span className={styles.requiredStar} aria-hidden="true">*</span>}
                            </label>
                            <textarea
                                id="confirm-modal-input"
                                className={styles.modalTextarea}
                                placeholder={inputPlaceholder}
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                aria-describedby={showCounter ? 'confirm-modal-count' : undefined}
                                autoFocus
                            />
                            {showCounter && (
                                <span
                                    id="confirm-modal-count"
                                    className={`${styles.counter} ${typed > 0 && inputTooShort ? styles.counterShort : ''} ${!inputTooShort ? styles.counterOk : ''}`}
                                    aria-live="polite"
                                >
                                    {tr("ui.confirm_min_chars", "{v0} / {v1} characters minimum", { v0: typed, v1: minLength })}
                                </span>
                            )}
                        </div>
                    )}
                </div>

                <div className={styles.modalFooter}>
                    <button type="button" className={styles.modalCancelBtn} onClick={onClose}>{cancelText}</button>
                    <button
                        type="button"
                        className={isDanger ? styles.modalRejectBtn : styles.modalConfirmBtn}
                        onClick={handleConfirmClick}
                        disabled={inputTooShort}
                    >
                        {confirmText}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};
